import BankIcon from 'assets/svg/icons/bankIcon';
import WalletEuroIcon from 'assets/svg/icons/wallet-euro-icon.svg?react';
import WalletPercentageIcon from 'assets/svg/icons/wallet-percentage.svg?react';
import { Chip } from 'components/atoms/Chip';
import { components as ReactSelectComponents } from 'components/atoms/Form/Select';
import { renderCustomSubAccountIcon } from 'components/atoms/Icons/SpaceIcons';
import {
  isAccountableBank,
  isTaxReserveAccountableBank,
} from 'components/pages/Banks/Wallet/helpers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select';
import type { Connector } from 'types/transactions.types';
import formatMoney from 'utils/formatMoney';

/**
 * ConnectorComponents - React Select components for displaying bank connectors
 *
 * This module provides custom React Select components for displaying bank connectors
 * in dropdown menus. It includes proper type safety and handles both external
 * transfers and internal connector accounts.
 *
 * Usage:
 * ```tsx
 * import { ConnectorComponents } from './ConnectorComponents';
 *
 * <Select
 *   components={{
 *     Option: ConnectorComponents.ConnectorOption,
 *     SingleValue: ConnectorComponents.ConnectorSingleValue,
 *   }}
 *   // ... other props
 * />
 * ```
 */

// Type definitions for option data
type ExternalOption = {
  value: 'external';
  label: string;
};

type ConnectorOption = {
  value: string;
  label: string;
} & Connector;

type OptionData = ExternalOption | ConnectorOption;

// Type guard functions
const hasValueProperty = (data: unknown): data is { value: unknown } => {
  return typeof data === 'object' && data !== null && 'value' in data;
};

const isExternalOption = (data: unknown): data is ExternalOption => {
  return hasValueProperty(data) && data.value === 'external';
};

const isConnectorOption = (data: unknown): data is ConnectorOption => {
  return (
    hasValueProperty(data) &&
    data.value !== 'external' &&
    typeof data.value === 'string' &&
    typeof data === 'object' &&
    '_id' in data &&
    'provider' in data
  );
};

/**
 * Get the appropriate icon for a connector based on its type
 * @param connector - The connector object
 * @returns React element representing the connector icon
 */
const getConnectorIcon = (connector: Connector) => {
  // First check if it's an accountable bank
  if (!isAccountableBank(connector)) {
    return <BankIcon className="w-6 h-6 text-bank-700" />;
  }

  if (connector.isCustomSubAccount && connector.icon) {
    return renderCustomSubAccountIcon(connector.icon, 'w-6 h-6');
  }
  if (isTaxReserveAccountableBank(connector)) {
    return <WalletPercentageIcon className="w-6 h-6 text-taxes-600" />;
  }
  return <WalletEuroIcon className="text-taxes-600" />;
};

// Custom Option component for displaying connector cards with chips and balance
const ConnectorOptionComponent: FC<OptionProps<OptionData>> = (props) => {
  const { t } = useTranslation();
  const { data } = props;

  // Special handling for external option
  if (isExternalOption(data)) {
    return (
      <ReactSelectComponents.Option {...props}>
        <div className="flex items-center justify-between w-full p-2">
          <div className="flex items-center space-x-3">
            <div className="flex flex-col items-center">
              <Chip
                className="bg-bank-100"
                id="external"
                label={t('accountable_banking.send_money.external')}
                icon={<BankIcon className="w-5 text-bank-700" />}
                size="sm"
                hoverEffect={false}
                shadowEffect={false}
              />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-primary text-base">
                {t('accountable_banking.send_money.external')}
              </h3>
            </div>
          </div>
        </div>
      </ReactSelectComponents.Option>
    );
  }

  if (isConnectorOption(data)) {
    // Determine chip background color based on connector type
    const chipClassName = data?.isCustomSubAccount
      ? 'bg-bank-100'
      : 'bg-taxes-100';

    return (
      <ReactSelectComponents.Option {...props}>
        <div className="flex items-center justify-between w-full p-2">
          <div className="flex items-center space-x-3">
            <div className="flex flex-col items-center">
              <Chip
                className={chipClassName}
                id={data._id}
                label={data.title || ''}
                icon={getConnectorIcon(data)}
                size="sm"
                hoverEffect={false}
                shadowEffect={false}
              />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-primary text-base">
                {data.title}
              </h3>
              <span className="text-sm text-primary-300">
                {formatMoney(
                  data?.balance?.amount as number,
                  data?.balance?.currency,
                )}
              </span>
            </div>
          </div>
        </div>
      </ReactSelectComponents.Option>
    );
  }

  return null;
};

// Custom SingleValue component for the select control
const ConnectorSingleValueComponent: FC<SingleValueProps<OptionData>> = (
  props,
) => {
  const { t } = useTranslation();
  const { data } = props;

  // Special handling for external option
  if (isExternalOption(data)) {
    return (
      <ReactSelectComponents.SingleValue {...props}>
        <div className="flex items-center space-x-3">
          <div className="flex flex-col items-center">
            <Chip
              className="bg-bank-100"
              id="external"
              label={t('accountable_banking.send_money.external')}
              icon={<BankIcon className="w-5 text-bank-700" />}
              size="sm"
              hoverEffect={false}
              shadowEffect={false}
            />
          </div>
          <div className="text-left">
            <span className="font-semibold text-primary text-sm">
              {t('accountable_banking.send_money.external')}
            </span>
          </div>
        </div>
      </ReactSelectComponents.SingleValue>
    );
  }

  if (isConnectorOption(data)) {
    // Determine chip background color based on connector type
    const chipClassName = data?.isCustomSubAccount
      ? 'bg-bank-100'
      : 'bg-taxes-100';

    return (
      <ReactSelectComponents.SingleValue {...props}>
        <div className="flex items-center space-x-3">
          <div className="flex flex-col items-center">
            <Chip
              className={chipClassName}
              id={data._id}
              label={data.title || ''}
              icon={getConnectorIcon(data)}
              size="sm"
              hoverEffect={false}
              shadowEffect={false}
            />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-primary text-sm">{data.title}</h3>
            <span className="text-sm text-primary-300">
              {formatMoney(
                data?.balance?.amount as number,
                data?.balance?.currency,
              )}
            </span>
          </div>
        </div>
      </ReactSelectComponents.SingleValue>
    );
  }

  return null;
};

/**
 * Main export object containing all connector components and utilities
 * This single export structure is compatible with Vite builds
 */
export const ConnectorComponents = {
  ConnectorOption: ConnectorOptionComponent,
  ConnectorSingleValue: ConnectorSingleValueComponent,
} as const;
